import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import { useDiscord } from '../../discord/useDiscord';
import { createChannelLobby } from '../../discord/DiscordProvider';
import { QuickRouter } from '../TwoD/QuickRouter';
import { API_URL, LOGO_URL } from '../../Settings';

import '../Menus/MainMenu/main-menu.css';
import './lobbies-page.css';


/**
 * CC_FE_PAGES (2026-06-01).
 *
 * The `/lobbies` picker. Holds the LiveTile grid that used to live in
 * MainMenu.js (CC_DISCORD_LIVE_TILES). On open-web it lists the public
 * lobbies fetched from GET /api/lobbies/public/ (CC_PUBLIC_LOBBY_BROWSER_FE,
 * 2026-06-07). On Discord-embedded it lists the channel-bound lobbies from
 * `discord.channelLobbies` first, then — CC_DISCORD_PUBLIC_DISCOVERY,
 * 2026-06-07 — a public-games section showing those same public lobbies
 * (deduped against the channel ones by uuid) so a Discord player can
 * DISCOVER and join a web-hosted public game without a shared link. Both
 * branches fall back to the empty-state placeholder when nothing is open.
 *
 * Navigation model (CC_FE_PAGES §2): clicking a tile navigates to
 * `/game/:lobbyUuid` and the GamePage mount IS the engagement act — it
 * fires the join/rejoin itself. The picker only routes; it does not POST
 * from_platform for a tile. New Game (Discord) creates the bound lobby
 * first (so it has a uuid to route to), then navigates.
 *
 * CC_FE_GAPS (2026-06-01): New Game (open-web) now navigates too. The
 * WS Members/Join payload carries the lobby uuid (backend
 * group_router.py), surfaced on the `group-update-event` detail by
 * GameGroupHandler. The open-web branch installs a one-shot listener,
 * fires Join(0), and routes to `/game/:lobbyUuid` off the next
 * broadcast — the "open-web deep entry deferred to a cross-platform CC"
 * note is closed. A Back affordance routes to the Main Menu.
 */

// CC_FE_GAPS (2026-06-01): bound the open-web New Game wait for the
// group-update-event that carries the new lobby's uuid. If nothing
// arrives in this window we drop the listener and stay on /lobbies
// rather than navigate somewhere wrong; the user can retry.
const NEW_GAME_NAV_TIMEOUT_MS = 3000;

const CHANNEL_LOBBY_STATE_FORMING = 'forming';
const OBSERVE_TOOLTIP = 'Observer mode coming soon';
const UNTITLED_HOTEL = 'Untitled hotel';
const REJOIN_STATUS_LINE = 'In progress — Rejoin';
const OBSERVER_STATUS_LINE = 'In progress — observer mode coming soon';
// CC_MIDGAME_RECRUITMENT (2026-06-07): the recruit affordance on a public
// PLAYING tile whose game has a claimable AI seat (the viewer holds none).
// COPY PLACEHOLDER — Michael owns the final wording.
const TAKE_SEAT_STATUS_LINE = 'In progress — Take this seat';
// CC_OBSERVER_ENTRY_V1 (2026-06-07): the watch affordance on a public
// PLAYING tile the viewer holds no seat in and that has no claimable AI
// seat. COPY PLACEHOLDER — Michael owns the final wording.
const WATCH_STATUS_LINE = 'In progress — Watch';
// Michael owns the exact copy; this is the §3.2 placeholder line.
const OPEN_WEB_EMPTY_LINE =
    'No public lobbies yet — use Friends / LFG or start a New Game';

// CC_DISCORD_PUBLIC_DISCOVERY (2026-06-07): the embedded list shows a
// public-games section below the channel's own lobbies. Michael owns the
// final wording — this is the flagged placeholder header for his pass.
const PUBLIC_SECTION_HEADER = 'COPY PLACEHOLDER';

// CC_PUBLIC_LOBBY_BROWSER_FE (2026-06-07): open-web public-lobby list
// endpoint. Wire contract (mirrored in CC_PUBLIC_LOBBY_BACKEND +
// CC_LIVING_GAMES_LIST_DESIGN):
//   GET /api/lobbies/public/ → {"lobbies": [{lobby_uuid, owner_name,
//   seats_open, seats_total, expansions, modified,
//   status, turn_count, scores, viewer_holds_seat}]}
// CC_LIVING_GAMES_LIST_DESIGN added the live-tile fields: `status`
// ('forming'|'playing'), `turn_count`, `scores` (by seat), and
// `viewer_holds_seat`. The list now includes public LAUNCHING/PLAYING
// games, not only FORMING ones. A PLAYING tile is actionable as Rejoin
// only for a viewer who holds a seat; the observe/recruit entry and the
// turn/score numerals are the named FE follow-up (the endpoint already
// carries the data — see CC_LIVING_GAMES_LIST_DESIGN §5).
const PUBLIC_LOBBIES_PATH = API_URL + 'lobbies/public/';

// Mirror of the backend LIVE_TILE_STATUS_PLAYING (lobbies/views.py): a
// public-list row's status when its game is live (LAUNCHING/PLAYING).
const PUBLIC_TILE_STATUS_PLAYING = 'playing';

// CC_MIDGAME_RECRUITMENT (2026-06-07): the mid-game recruit claim endpoint
// (POST /api/lobby/<uuid>/recruit_seat/, body {seat_index}). Django session
// auth + CSRF; the seat repoint lands server-side, then the FE routes into
// the game as the claimed seat (the ordinary join rail post-repoint).
const RECRUIT_SEAT_PATH = (lobbyUuid) =>
    API_URL + 'lobby/' + lobbyUuid + '/recruit_seat/';
const CSRF_HEADER_NAME = 'X-CSRFToken';


/**
 * Read the Django CSRF token from document.cookie, or null if absent.
 * Pure read; mirrors DiscordProvider's readCookie for the one POST this
 * page makes (the public list fetch is a GET and needs no token).
 */
function readCsrfToken() {
    if (typeof document === 'undefined' || !document.cookie) {
        return null;
    }
    const match = document.cookie.match(/(?:^|; )csrftoken=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
}

// Six faction keys in Action-Hex order. A lobby's tile color is picked
// deterministically from its UUID until the backend exposes a real
// per-lobby faction. The CSS variants .live-tile-<key> pull their color
// from the matching --<key> variable in hsh.css.
const FACTION_KEYS = ['deep', 'stem', 'hex', 'dread', 'honored', 'seed'];


/**
 * Deterministically pick one of the six faction keys from a lobby UUID.
 * Pure — the same UUID always yields the same key. Parses the first six
 * hex digits of the id mod 6. Falls back to the first key for a missing
 * or non-hex id.
 */
export function factionForUuid(uuid) {
    if (typeof uuid !== 'string' || uuid.length === 0) {
        return FACTION_KEYS[0];
    }
    const parsed = parseInt(uuid.slice(0, 6), 16);
    if (!Number.isFinite(parsed)) {
        return FACTION_KEYS[0];
    }
    return FACTION_KEYS[parsed % FACTION_KEYS.length];
}


/**
 * Resolve the owner's display name for a tile. Gates on the integer
 * owner id, never a username string (per
 * gate_on_integer_ids_not_strings): the owning seat is the one whose
 * user id equals the lobby owner's id, and that seat's
 * serializer-resolved `display_name` is authoritative. Falls back to
 * the lobby owner's own resolved display_name, then to a neutral label.
 */
function ownerNameFor(lobby) {
    if (!lobby) {
        return UNTITLED_HOTEL;
    }
    const owner = lobby.owner || null;
    const ownerId = (owner && owner.id != null)
        ? owner.id : lobby.owner_user_id;
    const seats = Array.isArray(lobby.seats) ? lobby.seats : [];
    if (ownerId != null) {
        const ownerSeat = seats.find(
            (s) => s && s.user && s.user.id === ownerId);
        if (ownerSeat && ownerSeat.display_name) {
            return ownerSeat.display_name;
        }
    }
    if (owner && owner.display_name) {
        return owner.display_name;
    }
    return UNTITLED_HOTEL;
}


/**
 * One short status sentence for a tile. Pure; branches on the entry's
 * derived displayState / userOwnsSeat (computed in DiscordProvider).
 */
export function statusLineFor(entry, userOwnsSeat) {
    const lobby = (entry && entry.lobby) || {};
    if (entry && entry.displayState === CHANNEL_LOBBY_STATE_FORMING) {
        const seats = Array.isArray(lobby.seats) ? lobby.seats : [];
        const filled = seats.filter(
            (s) => s && s.user && s.user.id).length;
        return `Forming — ${filled} / ${seats.length} seats`;
    }
    if (userOwnsSeat) {
        return REJOIN_STATUS_LINE;
    }
    return OBSERVER_STATUS_LINE;
}


/**
 * One faction-colored, whole-card-clickable tile. The tile IS the
 * affordance — clicking navigates to /game/:lobbyUuid. An in-progress
 * tile the viewer holds no seat in is disabled (observer mode lands in
 * a later CC).
 */
function LiveTile({ entry, index, onJoin }) {
    const lobby = entry.lobby || {};
    const lobbyUuid = entry.lobby_id;
    const faction = factionForUuid(lobby.uuid || lobbyUuid);
    const isObserverOnly =
        entry.displayState !== CHANNEL_LOBBY_STATE_FORMING
        && !entry.userOwnsSeat;
    const className = 'live-tile live-tile-' + faction
        + (isObserverOnly ? ' live-tile-disabled' : '');
    return (
        <button
            type="button"
            className={className}
            style={{ '--tile-index': index }}
            disabled={isObserverOnly}
            title={isObserverOnly ? OBSERVE_TOOLTIP : undefined}
            onClick={isObserverOnly ? undefined : () => onJoin(lobbyUuid)}
        >
            <div className="live-tile-art">
                <img
                    className="live-tile-logo"
                    src={LOGO_URL}
                    alt=""
                    aria-hidden="true"
                />
            </div>
            <div className="live-tile-name">{ownerNameFor(lobby)}</div>
            <div className="live-tile-status">
                {statusLineFor(entry, entry.userOwnsSeat)}
            </div>
        </button>
    );
}


// COPY PLACEHOLDER (CC_PUBLIC_LOBBY_BROWSER_FE, 2026-06-07): the open-web
// public-lobby tile's status line. Mirrors the Discord forming tile's
// "N / M seats" shape; Michael owns the final wording.
function publicSeatsLine(lobby) {
    const open = (lobby && Number.isFinite(lobby.seats_open))
        ? lobby.seats_open : 0;
    const total = (lobby && Number.isFinite(lobby.seats_total))
        ? lobby.seats_total : 0;
    return `${open} / ${total} seats open`;
}


/**
 * The recruitable seat indexes a public-list row exposes, as an array
 * (CC_MIDGAME_RECRUITMENT). Empty for any row that carries none — a
 * FORMING row, a PLAYING row with no claimable AI seat, or a pre-recruit
 * payload that omits the field. Pure.
 */
function recruitableSeatsOf(lobby) {
    return (lobby && Array.isArray(lobby.recruitable_seats))
        ? lobby.recruitable_seats : [];
}


/**
 * One short status sentence for an open-web public-lobby tile. A FORMING
 * row shows the "N / M seats open" line; a live (PLAYING) row the viewer
 * holds a seat in shows Rejoin; one they do NOT hold a seat in shows the
 * recruit line when it has a claimable AI seat (CC_MIDGAME_RECRUITMENT),
 * else the observer coming-soon line — mirroring the Discord LiveTile's
 * statusLineFor. Pure.
 */
export function publicStatusLine(lobby) {
    if (lobby && lobby.status === PUBLIC_TILE_STATUS_PLAYING) {
        if (lobby.viewer_holds_seat) {
            return REJOIN_STATUS_LINE;
        }
        if (recruitableSeatsOf(lobby).length > 0) {
            return TAKE_SEAT_STATUS_LINE;
        }
        // CC_OBSERVER_ENTRY_V1: no seat to rejoin, no claimable AI seat —
        // the game is public and live, so it is watchable (was the
        // "observer mode coming soon" placeholder pre-observer).
        return WATCH_STATUS_LINE;
    }
    return publicSeatsLine(lobby);
}


/**
 * One open-web public-lobby tile. Reuses the Main Menu live-tile visual
 * contract verbatim (the same classes the Discord LiveTile uses) so the
 * open-web list matches the embedded list with no new layout. The whole
 * card is the affordance: clicking routes to /game/:lobbyUuid, where the
 * GamePage mount claims a seat (the landed browser join rail). Expansions
 * are omitted from the tile — the Discord tile shape has no slot for them,
 * and the prompt forbids new layout design.
 *
 * CC_LIVING_GAMES_LIST_DESIGN / CC_MIDGAME_RECRUITMENT: the list carries
 * live (PLAYING) rows. A PLAYING tile is a Rejoin for a viewer who holds a
 * seat (`viewer_holds_seat`). For a viewer who holds none: if the game has
 * a claimable AI seat (`recruitable_seats`) the tile is "Take this seat" —
 * clicking POSTs the recruit claim, then routes into the game as the
 * claimed seat (the ordinary join rail post-repoint). With no claimable
 * seat it stays a disabled "coming soon" observer tile (the same pattern
 * the Discord LiveTile uses), never a join the rail would reject.
 */
function PublicLobbyTile({ lobby, index, onJoin, onRecruit }) {
    const lobbyUuid = lobby.lobby_uuid;
    const faction = factionForUuid(lobbyUuid);
    const isPlaying = lobby.status === PUBLIC_TILE_STATUS_PLAYING;
    const holdsSeat = !!lobby.viewer_holds_seat;
    const recruitableSeats = recruitableSeatsOf(lobby);
    const canRecruit =
        isPlaying && !holdsSeat && recruitableSeats.length > 0;
    // CC_OBSERVER_ENTRY_V1: a public PLAYING game the viewer holds no seat
    // in and that has no claimable AI seat is now WATCHABLE — the whole
    // card routes to /game/:uuid, where GamePage's observe handler enters
    // SPECTATING and mounts the board read-only. Recruitment (Take this
    // seat) still wins when a claimable AI seat exists; a seat-holder
    // rejoins; a FORMING tile joins. Every public tile is now actionable,
    // so there is no disabled "coming soon" state on this list anymore.
    const className = 'live-tile live-tile-' + faction;
    let onClick;
    if (canRecruit) {
        onClick = () => onRecruit(lobbyUuid, recruitableSeats[0]);
    } else {
        onClick = () => onJoin(lobbyUuid);
    }
    return (
        <button
            type="button"
            className={className}
            style={{ '--tile-index': index }}
            onClick={onClick}
        >
            <div className="live-tile-art">
                <img
                    className="live-tile-logo"
                    src={LOGO_URL}
                    alt=""
                    aria-hidden="true"
                />
            </div>
            <div className="live-tile-name">
                {lobby.owner_name || UNTITLED_HOTEL}
            </div>
            <div className="live-tile-status">{publicStatusLine(lobby)}</div>
        </button>
    );
}


export default function LobbiesPage({ app }) {
    const discord = useDiscord();
    const channelLobbies = Array.isArray(discord.channelLobbies)
        ? discord.channelLobbies : [];
    const channelId = discord.channelId;
    const guildId = discord.guildId;
    const instanceId = discord.instanceId;
    const isEmbedded = discord.isEmbedded;
    const hasChannelTiles = isEmbedded && channelLobbies.length > 0;

    // CC_PUBLIC_LOBBY_BROWSER_FE (2026-06-07): the public-lobby list.
    // Empty until the on-mount fetch resolves; an empty result (or a
    // failed fetch) leaves the existing placeholder in place.
    // CC_DISCORD_PUBLIC_DISCOVERY (2026-06-07): the embedded branch ALSO
    // consumes this — it renders the public-games section below the
    // channel-bound lobbies — so the fetch now runs in both contexts.
    const [publicLobbies, setPublicLobbies] = useState([]);

    useEffect(() => {
        // Fetch the public list once on mount in BOTH contexts: open-web
        // renders it as the whole list; embedded renders it as the
        // public-games section under discord.channelLobbies. No polling
        // (setInterval is banned, HSH_STYLE.md §Data Fetching); the list is
        // a snapshot the user re-enters /lobbies to refresh, and
        // LobbiesPage holds no persistent WS lobby-list subscription to
        // ride for live updates — same snapshot story in both contexts.
        if (typeof fetch !== 'function') {
            return undefined;
        }
        let cancelled = false;
        const loadPublicLobbies = async () => {
            try {
                const response = await fetch(PUBLIC_LOBBIES_PATH, {
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok) {
                    return;
                }
                const payload = await response.json();
                if (cancelled) {
                    return;
                }
                const lobbies = Array.isArray(payload && payload.lobbies)
                    ? payload.lobbies : [];
                setPublicLobbies(lobbies);
            } catch (error) {
                // Network / parse failure leaves the list empty so the
                // open-web placeholder renders; re-enter /lobbies to retry.
            }
        };
        loadPublicLobbies();
        return () => {
            cancelled = true;
        };
    }, [discord.isEmbedded]);

    // CC_DISCORD_PUBLIC_DISCOVERY (2026-06-07): dedup the public-games
    // section against the channel-bound lobbies so a co-bound lobby (a web
    // game a Discord player refreshed into — see CC_CROSS_PLATFORM_PLAY
    // hand-back 6.2) never repeats below its own channel tile. Gate on the
    // lobby uuid: a channel entry carries it on `lobby_id` and on
    // `lobby.uuid`; collect both so either shape matches the public row's
    // `lobby_uuid`. Open-web has no channelLobbies, so the set is empty and
    // the public list passes through whole.
    const channelLobbyUuids = new Set();
    channelLobbies.forEach((entry) => {
        if (entry && entry.lobby_id) {
            channelLobbyUuids.add(entry.lobby_id);
        }
        if (entry && entry.lobby && entry.lobby.uuid) {
            channelLobbyUuids.add(entry.lobby.uuid);
        }
    });
    const publicSectionLobbies = publicLobbies.filter(
        (lobby) => lobby && !channelLobbyUuids.has(lobby.lobby_uuid));
    const hasPublicSection = publicSectionLobbies.length > 0;
    const hasAnyTiles = hasChannelTiles || hasPublicSection;

    const onBack = useCallback(() => {
        // CC_FE_GAPS (2026-06-01): /lobbies had no way out but the URL
        // bar. One button, one route to the Main Menu.
        window.dispatchEvent(new CustomEvent(
            'quick-router-event', { detail: '/' }));
    }, []);

    const onJoin = useCallback((lobbyUuid) => {
        // Route to the canonical surface; GamePage's mount fires the
        // actual join/rejoin for this uuid.
        window.dispatchEvent(new CustomEvent(
            'quick-router-event', { detail: '/game/' + lobbyUuid }));
    }, []);

    const onRecruit = useCallback((lobbyUuid, seatIndex) => {
        // CC_MIDGAME_RECRUITMENT (2026-06-07): claim a public PLAYING
        // game's AI seat. POST the repoint, then route to /game/:uuid where
        // the ordinary join rail carries the recruit in as the now-owned
        // seat (the GamePlayer resolves to them post-repoint). On any
        // non-2xx / transport failure we stay on /lobbies (retryable)
        // rather than route into a game the rail would bounce.
        const recruit = async () => {
            if (typeof fetch !== 'function') {
                return;
            }
            try {
                const csrfToken = readCsrfToken();
                const headers = {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                };
                if (csrfToken) {
                    headers[CSRF_HEADER_NAME] = csrfToken;
                }
                const response = await fetch(RECRUIT_SEAT_PATH(lobbyUuid), {
                    method: 'POST',
                    credentials: 'include',
                    headers,
                    body: JSON.stringify({ seat_index: seatIndex }),
                });
                if (!response.ok) {
                    console.warn('[LobbiesPage] recruit_seat rejected'
                        + ' status=%s — staying on /lobbies',
                        response.status);
                    return;
                }
                window.dispatchEvent(new CustomEvent(
                    'quick-router-event',
                    { detail: '/game/' + lobbyUuid }));
            } catch (error) {
                console.error('[LobbiesPage] recruit_seat transport'
                    + ' error: %s', error && error.message);
            }
        };
        recruit();
    }, []);

    const onNewGame = useCallback(() => {
        if (discord.isEmbedded && channelId) {
            // Discord: mint a fresh channel-bound lobby, then route to
            // its uuid. createChannelLobby returns the seated payload and
            // also fires discord-lobby-joined, which GamePage reads.
            createChannelLobby(channelId, guildId, instanceId)
                .then((payload) => {
                    const uuid = payload && payload.lobby
                        && payload.lobby.uuid;
                    if (uuid) {
                        window.dispatchEvent(new CustomEvent(
                            'quick-router-event',
                            { detail: '/game/' + uuid }));
                    }
                });
            return;
        }
        // Open web: mint a fresh Browser-bound lobby on the WS rail.
        // CC_FE_GAPS (2026-06-01): the Members/Join broadcast now carries
        // the lobby uuid (surfaced on group-update-event by
        // GameGroupHandler), so we route to /game/:lobbyUuid the moment
        // it arrives. Install the one-shot listener BEFORE firing Join(0)
        // to avoid racing the broadcast; bound the wait so a silent
        // backend leaves the user on /lobbies (retryable) rather than
        // stranded.
        if (!(app && app.game && app.game.gameApi)) {
            return;
        }
        let timeoutId = null;
        const onGroupUpdate = (event) => {
            const detail = (event && event.detail) || {};
            if (!detail.lobbyUuid) {
                return;
            }
            window.removeEventListener('group-update-event', onGroupUpdate);
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            window.dispatchEvent(new CustomEvent(
                'quick-router-event',
                { detail: '/game/' + detail.lobbyUuid }));
        };
        window.addEventListener('group-update-event', onGroupUpdate);
        timeoutId = setTimeout(() => {
            window.removeEventListener('group-update-event', onGroupUpdate);
            console.warn('[LobbiesPage] New Game: no group-update-event'
                + ' with lobbyUuid within %sms — staying on /lobbies',
                NEW_GAME_NAV_TIMEOUT_MS);
        }, NEW_GAME_NAV_TIMEOUT_MS);
        app.game.gameApi.joinGroup(0);
    }, [discord.isEmbedded, channelId, guildId, instanceId, app]);

    return (
        <div className="lobbies-page">
            <QuickRouter />
            <Button
                variant="honored"
                size="sm"
                className="lobbies-page-back"
                onClick={onBack}
            >
                Back
            </Button>
            {hasChannelTiles && (
                <div className="live-tiles">
                    {channelLobbies.map((entry, index) => (
                        <LiveTile
                            key={entry.lobby_id}
                            entry={entry}
                            index={index}
                            onJoin={onJoin}
                        />
                    ))}
                </div>
            )}
            {hasPublicSection && (
                <>
                    {isEmbedded && (
                        <h2 className="lobbies-page-section">
                            {PUBLIC_SECTION_HEADER}
                        </h2>
                    )}
                    <div className="live-tiles">
                        {publicSectionLobbies.map((lobby, index) => (
                            <PublicLobbyTile
                                key={lobby.lobby_uuid}
                                lobby={lobby}
                                index={index}
                                onJoin={onJoin}
                                onRecruit={onRecruit}
                            />
                        ))}
                    </div>
                </>
            )}
            {!hasAnyTiles && (
                <p className="lobbies-page-empty">{OPEN_WEB_EMPTY_LINE}</p>
            )}
            <div className="lobbies-page-actions">
                <Button
                    variant="hex"
                    className="main-menu-item"
                    onClick={onNewGame}
                >
                    New Game
                </Button>
            </div>
        </div>
    );
}
